import { WbGridCol, WbGridRow, WbHeading, WbPagination, WbText } from '@workbench/react';
import { useTranslation } from '../../common/i18n/useTranslation';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { grey as greyColor } from '@mercedes-benz/mui5-theme/build/main/lib/Colors';
import NewsCard from './NewsCard';
import { useRestOperations } from '../../common/rest/restOperations';
import { ApiUrls } from '../../../api/api/apiUrls';
import { useEffect, useState } from 'react';
import { SortDirection } from '../../utils/enums';
import { initialPagination, MAX_PAGE_SIZE } from '../../utils/pagination';
import { nameof } from '@plusng/react-common-components';
import { useTranslationContext } from '../../common/i18n/TranslationProvider';
import { SupportedLanguages } from '../../common/i18n/SupportedLanguages';
import { dayTimeFormatter } from '../../common/formatters';
import { WbPaginationCustomEvent } from '@workbench/core';
import BeamSelect from '../../common/components/input/beamSelect/BeamSelect';
import { PageNewsDto, NewsDto, Pageable, NewContentDto } from '../../api';

const useStyles = makeStyles()((theme: Theme) => ({
    grid: {
        backgroundColor: greyColor[95],
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        marginBottom: theme.spacing(4),
    },
    gridCol: {
        flexDirection: 'column',
        width: '100%',
    },
    newsGridHeader: {
        marginBottom: theme.spacing(2),
    },
    textGridHeader: {
        marginTop: theme.spacing(2),
        color: greyColor[35],
    },
    NoNews: {
        margin: theme.spacing(8),

        justifyContent: 'center',
        display: 'flex',
    },
    pagination: {
        display: 'grid',
        justifyContent: 'end',
        marginTop: theme.spacing(2),
    },
}));

function NewsSection() {
    const translate = useTranslation();
    const { classes } = useStyles();
    const { doGetRequest } = useRestOperations<PageNewsDto>();
    const { langState } = useTranslationContext();

    const [news, setNews] = useState<NewsDto[]>([] as NewsDto[]);
    const [pageNumber, setPageNumber] = useState<number | undefined>(0);
    const [totalPages, setTotalPages] = useState<number | undefined>(0);

    const visibleEntries: string[] = ['10', '20', translate('all')];
    const visibleEntriesDefault = 5;
    const all = MAX_PAGE_SIZE;

    const [pagination, setPagination] = useState<Pageable>({
        ...initialPagination,
        size: visibleEntriesDefault,
        sort: [`${nameof<NewsDto>('publishDate')},${SortDirection.DESC}`],
    });
    const [entries, setEntries] = useState(visibleEntriesDefault.toString());
    const getNews = async () => {
        const result = await doGetRequest({
            queryString: ApiUrls.userApi.getNews(pagination),
        });

        if (result) {
            const page = result as PageNewsDto;
            const newsResponse = (result as PageNewsDto).content ?? [];
            setNews(newsResponse);
            setPageNumber(page.pageable?.pageNumber);
            setTotalPages(page.totalPages);
        }
    };

    useEffect(() => {
        getNews();
    }, [pagination]);

    const handleOnChangeSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const data = event.target.value;
        if (data !== translate('all')) {
            setPagination({ page: 0, size: parseInt(data), sort: pagination.sort });
            setEntries(data);
        } else {
            setPagination({ page: 0, size: all, sort: pagination.sort });
            setEntries('All');
        }
    };

    const handleOnchangePage = (event: WbPaginationCustomEvent<number>) => {
        setPagination({ page: event.detail - 1, size: pagination.size, sort: pagination.sort });
    };

    const getNewsByLanguage = (contents: NewContentDto[]): NewContentDto | undefined =>
        contents.find((news) =>
            langState === SupportedLanguages.de ? news.contentLanguage === 'DE' : news.contentLanguage === 'EN'
        );

    return (
        <div className={classes.grid}>
            <WbGridRow>
                <WbGridCol mq1={8}>
                    <WbGridRow className={classes.newsGridHeader}>
                        <WbGridCol mq1={9}>
                            <WbHeading size="l" className={classes.textGridHeader}>
                                {translate('news')}
                            </WbHeading>
                        </WbGridCol>
                        {news.length > 0 && (
                            <WbGridCol mq1={3}>
                                <BeamSelect
                                    value={entries}
                                    label={translate('visible_entries')}
                                    onChange={handleOnChangeSelect}
                                >
                                    <option value={visibleEntriesDefault}>{visibleEntriesDefault}</option>
                                    {visibleEntries.map((item, index) => (
                                        <option key={item} value={visibleEntries[index]}>
                                            {visibleEntries[index]}
                                        </option>
                                    ))}
                                </BeamSelect>
                            </WbGridCol>
                        )}
                    </WbGridRow>
                    {news.length > 0 ? (
                        <WbGridCol mq1={8} className={classes.gridCol}>
                            {news.map((content) => (
                                <NewsCard
                                    key={content.id}
                                    date={dayTimeFormatter(content.publishDate ?? '', translate('dateTimeFormat'))}
                                    title={getNewsByLanguage(content.contents)?.title}
                                    newsId={content.id}
                                />
                            ))}
                        </WbGridCol>
                    ) : (
                        <WbGridCol className={classes.NoNews}>
                            <WbText size="l">{translate('no_messages')}</WbText>
                        </WbGridCol>
                    )}
                    {pageNumber !== undefined && pagination.size !== undefined && pagination.size < all && (
                        <WbGridCol className={classes.pagination}>
                            <WbPagination
                                compact
                                currentPage={pageNumber + 1}
                                pages={totalPages}
                                onWbchange={handleOnchangePage}
                            />
                        </WbGridCol>
                    )}
                </WbGridCol>
            </WbGridRow>
        </div>
    );
}

export default NewsSection;
